import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PlayCircle, BookOpen, Sparkles, ArrowRight, Award } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/courses").then(({ data }) => setCourses(Array.isArray(data) ? data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const downloadCert = async (c) => {
    try {
      const res = await fetch(`${API}/certificate/${c.id}`, { credentials: "include" });
      if (!res.ok) { toast.error("Completa todas las lecciones primero"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `certificado-${c.slug}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("¡Certificado descargado!");
    } catch { toast.error("No se pudo descargar el certificado"); }
  };

  return (
    <div className="App">
      <Navbar />
      <section className="pt-28 pb-16 max-w-7xl mx-auto px-6">
        <span className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">// Mi panel</span>
        <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tighter mt-3 mb-2">Hola, {user?.name?.split(" ")[0] || "estudiante"}</h1>
        <p className="font-body text-[#8A8D98] mb-12">Continúa donde lo dejaste.</p>

        <div className="flex items-center gap-3 mb-6">
          <BookOpen className="w-5 h-5 text-[#00E5FF]" />
          <h2 className="font-heading font-semibold text-xl">Mis cursos</h2>
          <span className="font-mono text-sm text-[#525560]">({courses.length})</span>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><div className="w-10 h-10 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" /></div>
        ) : courses.length === 0 ? (
          <div className="bg-[#0C0D12] border border-white/10 rounded-xl p-12 text-center">
            <Sparkles className="w-10 h-10 text-[#00E5FF] mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-xl mb-2">Aún no tienes cursos</h3>
            <p className="font-body text-[#8A8D98] mb-6">Explora el catálogo y comienza tu aprendizaje.</p>
            <Button data-testid="dashboard-explore-btn" onClick={() => navigate("/cursos")} className="bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md">
              Explorar cursos <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((c) => (
              <div key={c.id} data-testid={`enrolled-${c.slug}`} className="bg-[#0C0D12] border border-white/5 rounded-lg overflow-hidden group">
                <div className="relative h-40 overflow-hidden">
                  <img src={c.image_url} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C0D12] to-transparent" />
                </div>
                <div className="p-6">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#00E5FF]">{c.category}</span>
                  <h3 className="font-heading font-semibold text-lg mt-2 mb-4 line-clamp-2">{c.title}</h3>
                  <div className="mb-4">
                    <div className="flex items-center justify-between font-mono text-xs text-[#8A8D98] mb-1.5">
                      <span>Progreso</span><span data-testid={`progress-${c.slug}`}>{c.progress}%</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#00E5FF] transition-all duration-500" style={{ width: `${c.progress}%` }} />
                    </div>
                  </div>
                  <Button data-testid={`continue-${c.slug}`} onClick={() => navigate(`/aprender/${c.slug}`)} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md mb-2">
                    <PlayCircle className="w-4 h-4 mr-2" /> {c.progress > 0 ? "Continuar" : "Empezar"}
                  </Button>
                  {c.progress === 100 && (
                    <Button data-testid={`cert-${c.slug}`} onClick={() => downloadCert(c)} variant="outline" className="w-full border-[#00E6BA]/40 bg-[#00E6BA]/5 text-[#00E6BA] hover:bg-[#00E6BA]/10 rounded-md">
                      <Award className="w-4 h-4 mr-2" /> Certificado
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
